/**
 * Certificación SII — pantalla interna (platform admin), tenant-scoped.
 *
 * Lista las corridas de certificación del tenant, permite ingestar un set del SII
 * (pegar/subir el TXT) y operar la etapa de intercambio (Ley 19.983). El detalle de
 * cada corrida vive en CertificationRunDetailPage.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Loader2, Upload, ArrowLeftRight, FileCheck2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader, type HelpContent } from '@/components/HelpPanel'
import { inputCls, labelCompactCls } from '@/lib/formStyles'
import { extractErrorMessage } from '@/lib/errorUtils'
import { Modal } from '@/components/ds/Modal'
import {
  useCertRuns, useIngestSet, useIntercambio,
  type CertRunList,
} from '@/modules/certification/hooks/useCertification'

const HELP: HelpContent = {
  title: 'Certificación SII',
  sections: [
    { icon: 'purpose', title: 'Qué es', content: 'Herramienta INTERNA para certificar a un cliente ante el SII de Chile generando los SETs de prueba con el motor de emisión de Clobi. Opera sobre el tenant del cliente (su certificado y sus CAF de certificación) en ambiente de homologación.' },
    { icon: 'how', title: 'Flujo', content: '1. Ingesta el set del SII (pega o sube el TXT).\n2. Carga los CAF de certificación por tipo.\n3. Define el receptor de prueba y el mapeo ítem→producto.\n4. Genera y firma el envío (un solo sobre).\n5. Envía al SII (un trackid) y consulta el estado.\n6. Registra el resultado del SII y, al autorizar, aplica la resolución al tenant.' },
    { icon: 'info', title: 'Intercambio (Ley 19.983)', content: 'El primer proceso exige certificar el intercambio: sube el EnvioDTE de muestra del SII y descarga el acuse de recibo del envío firmado para presentarlo al SII.' },
    { icon: 'tip', title: 'Folios de certificación', content: 'Los CAF de certificación son SEPARADOS de los de producción: un set de prueba nunca consume folios reales del tenant.' },
  ],
}

const RUN_STATUS_STYLES: Record<string, string> = {
  draft: 'bg-glass-3 text-content-muted',
  generated: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  sent: 'bg-warning-surface-strong text-warning-content',
  accepted: 'bg-success-surface-strong text-success-content',
  rejected: 'bg-danger-surface-strong text-danger-content',
  error: 'bg-danger-surface-strong text-danger-content',
}

const RESULT_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-glass-3 text-content-muted',
  cumple: 'bg-success-surface-strong text-success-content',
  reparo: 'bg-warning-surface-strong text-warning-content',
  rechazado: 'bg-danger-surface-strong text-danger-content',
}

export function StatusBadge({ label, styleKey, styles }: {
  label: string; styleKey: string; styles: Record<string, string>
}) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[styleKey] || styles.draft || ''}`}>
      {label}
    </span>
  )
}

// ── Modal de ingesta del set ─────────────────────────────────────────────────

function IngestModal({ onClose, onDone }: {
  onClose: () => void
  onDone: (data: { results: CertRunList[]; count: number }) => void
}) {
  const [txt, setTxt] = useState('')
  const [error, setError] = useState('')
  const ingest = useIngestSet()

  const readFile = (file: File) => {
    // El set del SII suele venir en ISO-8859-1 (Latin-1). Intentamos UTF-8 y, si
    // aparecen bytes inválidos (carácter de reemplazo U+FFFD), releemos como Latin-1.
    const reader = new FileReader()
    reader.onload = () => {
      const utf8 = String(reader.result || '')
      if (utf8.includes('�')) {
        const r2 = new FileReader()
        r2.onload = () => setTxt(String(r2.result || ''))
        r2.readAsText(file, 'ISO-8859-1')
      } else {
        setTxt(utf8)
      }
    }
    reader.readAsText(file)
  }

  const submit = () => {
    setError('')
    ingest.mutate(txt, {
      onSuccess: (data) => onDone(data),
      onError: (e) => setError(extractErrorMessage(e, 'No se pudo ingestar el set.')),
    })
  }

  return (
    <Modal open onClose={onClose} title="Nueva certificación — ingestar set del SII" size="2xl"
      actions={<>
        <button onClick={onClose} className="rounded-lg border border-divider px-3 py-1.5 text-sm text-content-muted hover:bg-soft">Cancelar</button>
        <button onClick={submit} disabled={ingest.isPending || !txt.trim()}
          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
          {ingest.isPending && <Loader2 size={14} className="animate-spin" />} Ingestar
        </button>
      </>}
    >
      <div className="space-y-3">
        {error && <div className="rounded-lg bg-danger-surface px-3 py-2 text-sm text-danger-content">{error}</div>}
        <div>
          <label className={labelCompactCls}>Archivo del set (.txt)</label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-divider px-3 py-1.5 text-sm text-content-muted hover:bg-soft">
            <Upload size={14} /> Seleccionar archivo
            <input type="file" accept=".txt,text/plain" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f) }} />
          </label>
        </div>
        <div>
          <label htmlFor="certificationpage-o-pega-el-contenido-del-set" className={labelCompactCls}>O pega el contenido del set</label>
          <textarea id="certificationpage-o-pega-el-contenido-del-set" value={txt} onChange={e => setTxt(e.target.value)} rows={12}
            placeholder="SET BASICO - NUMERO DE ATENCION: ..."
            className={`${inputCls} font-mono text-xs`} />
        </div>
      </div>
    </Modal>
  )
}

// ── Panel de intercambio (Ley 19.983) ────────────────────────────────────────

function IntercambioPanel() {
  const [xml, setXml] = useState('')
  const [idResp, setIdResp] = useState(1)
  const [error, setError] = useState('')
  const [ok, setOk] = useState(false)
  const intercambio = useIntercambio()

  const readFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => setXml(String(reader.result || ''))
    reader.readAsText(file, 'ISO-8859-1')
  }

  const submit = () => {
    setError(''); setOk(false)
    intercambio.mutate({ envio_xml: xml, id_respuesta: idResp }, {
      onSuccess: () => setOk(true),
      onError: (e) => setError(extractErrorMessage(e, 'No se pudo generar la respuesta.')),
    })
  }

  return (
    <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-5 shadow-card">
      <div className="mb-3 flex items-center gap-2">
        <ArrowLeftRight size={16} className="text-primary" />
        <h2 className="text-sm font-semibold text-content">Intercambio (Ley 19.983) — acuse de recibo del envío</h2>
      </div>
      <p className="mb-3 text-xs text-content-muted">
        Sube el <strong>EnvioDTE de muestra</strong> que envía el SII y descarga el acuse de recibo del
        envío firmado con el certificado del tenant. Presenta ese XML al SII.
      </p>
      {error && <div className="mb-3 rounded-lg bg-danger-surface px-3 py-2 text-sm text-danger-content">{error}</div>}
      {ok && <div className="mb-3 rounded-lg bg-success-surface px-3 py-2 text-sm text-success-content">Respuesta firmada generada y descargada.</div>}
      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <label className={labelCompactCls}>EnvioDTE de muestra</label>
          <div className="mb-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-divider px-3 py-1.5 text-sm text-content-muted hover:bg-soft">
              <Upload size={14} /> Subir XML
              <input type="file" accept=".xml,text/xml,application/xml" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f) }} />
            </label>
          </div>
          <textarea aria-label="XML del documento" value={xml} onChange={e => setXml(e.target.value)} rows={5}
            placeholder="<EnvioDTE ...>...</EnvioDTE>" className={`${inputCls} font-mono text-xs`} />
        </div>
        <div className="space-y-2">
          <div>
            <label htmlFor="certificationpage-id-respuesta" className={labelCompactCls}>ID Respuesta</label>
            <input id="certificationpage-id-respuesta" type="number" min={1} value={idResp} onChange={e => setIdResp(Number(e.target.value) || 1)}
              className={`${inputCls} w-28`} />
          </div>
          <button onClick={submit} disabled={intercambio.isPending || !xml.trim()}
            className="inline-flex w-full items-center justify-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
            {intercambio.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileCheck2 size={14} />}
            Generar y descargar
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Página ────────────────────────────────────────────────────────────────────

export function CertificationPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useCertRuns()
  const [showIngest, setShowIngest] = useState(false)
  const [notice, setNotice] = useState('')

  const runs: CertRunList[] = data?.results ?? []

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <PageHeader
        title="Certificación SII"
        subtitle={`${runs.length} ${runs.length === 1 ? 'corrida' : 'corridas'} · uso interno (platform admin)`}
        help={HELP}
      >
        <button onClick={() => setShowIngest(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-hover">
          <Plus size={16} /> Nueva certificación
        </button>
      </PageHeader>

      {notice && (
        <div className="flex items-start justify-between gap-3 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
          <span>{notice}</span>
          <button onClick={() => setNotice('')} className="shrink-0 text-blue-500 hover:text-blue-700">✕</button>
        </div>
      )}

      <DataTable
        columns={[
          { key: 'numero_atencion', header: 'N° Atención', render: r => <span className="font-medium text-content">{r.numero_atencion}</span> },
          { key: 'set_type', header: 'Tipo de set', render: r => r.set_type || '—' },
          {
            key: 'status', header: 'Estado envío',
            render: r => <StatusBadge label={r.status_display} styleKey={r.status} styles={RUN_STATUS_STYLES} />,
          },
          {
            key: 'result_status', header: 'Resultado',
            render: r => <StatusBadge label={r.result_status_display} styleKey={r.result_status} styles={RESULT_STATUS_STYLES} />,
          },
          { key: 'cases_count', header: 'Casos', numeric: true },
          {
            key: 'created_at', header: 'Creado',
            render: r => <span className="text-content-subtle">{new Date(r.created_at).toLocaleDateString('es-CL')}</span>,
          },
        ]}
        rows={runs}
        rowKey={r => r.id}
        onRowClick={r => navigate(`/certification/${r.id}`)}
        loading={isLoading}
        empty={<EmptyState icon={Loader2} size="sm" title="Sin corridas de certificación." hint="Crea una con «Nueva certificación»." />}
        minWidth={760}
        caption="Corridas de certificación ante el SII"
      />

      <IntercambioPanel />

      {showIngest && (
        <IngestModal
          onClose={() => setShowIngest(false)}
          onDone={(data) => {
            setShowIngest(false)
            if (data.count === 1 && data.results[0]) {
              navigate(`/certification/${data.results[0].id}`)
            } else {
              const nombres = data.results.map(r => r.set_type || `N°${r.numero_atencion}`).join(', ')
              setNotice(
                `El archivo contenía ${data.count} sets: se creó una corrida por cada uno (${nombres}). ` +
                'Cada set se genera y envía al SII por separado (el SII exige envíos independientes). ' +
                'Ábrelos desde la lista de abajo.',
              )
            }
          }}
        />
      )}
    </div>
  )
}
